import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { botService } from '../services';
import '../styles/BilibiliLogin.css';

/**
 * B站扫码登录组件
 * @param {Object} props
 * @param {Function} props.onLoginSuccess - 登录成功回调，返回 { cookie, mid, uname, face }
 * @param {Function} props.onCancel - 取消登录回调
 * @param {Object} props.currentAccount - 当前已绑定的账号信息
 */
const BilibiliQRLogin = ({ onLoginSuccess, onCancel, currentAccount }) => {
  const [step, setStep] = useState('idle'); // idle | loading | qrcode | scanned | success | error | expired
  const [qrcodeUrl, setQrcodeUrl] = useState('');
  const [qrcodeKey, setQrcodeKey] = useState('');
  const [message, setMessage] = useState('');
  const [loginResult, setLoginResult] = useState(null);
  const [countdown, setCountdown] = useState(180);
  
  const pollIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // 清理定时器
  const cleanup = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // 组件卸载时清理
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // 生成二维码
  const generateQRCode = async () => {
    cleanup();
    setStep('loading');
    setMessage('正在生成二维码...');
    setCountdown(180);

    try {
      const result = await botService.generateQRCode();
      
      if (result.success) {
        setQrcodeUrl(result.url);
        setQrcodeKey(result.qrcode_key);
        setStep('qrcode');
        setMessage('请使用B站App扫描二维码');
        
        // 开始轮询
        startPolling(result.qrcode_key);
        
        // 开始倒计时
        countdownIntervalRef.current = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              cleanup();
              setStep('expired');
              setMessage('二维码已过期，请重新生成');
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setStep('error');
        setMessage(result.message || '生成二维码失败');
      }
    } catch (error) {
      console.error('生成二维码失败:', error);
      setStep('error');
      setMessage(error.response?.data?.message || '生成二维码失败，请重试');
    }
  };

  // 轮询二维码状态
  const startPolling = (key) => {
    pollIntervalRef.current = setInterval(async () => {
      try {
        const result = await botService.pollQRCodeStatus(key);
        
        if (result.success && result.status === 'success') {
          // 登录成功
          cleanup();
          setStep('success');
          setMessage('登录成功！');
          setLoginResult({
            cookie: result.cookie,
            mid: result.mid,
            uname: result.userInfo?.name || result.userInfo?.uname,
            face: result.userInfo?.face
          });
          
          // 回调
          if (onLoginSuccess) {
            onLoginSuccess({
              cookie: result.cookie,
              mid: result.mid,
              uname: result.userInfo?.name || result.userInfo?.uname,
              face: result.userInfo?.face
            });
          }
        } else {
          switch (result.status) {
            case 'scanned':
              setStep('scanned');
              setMessage('已扫码，请在手机上确认登录');
              break;
            case 'expired':
              cleanup();
              setStep('expired');
              setMessage('二维码已过期，请重新生成');
              break;
            case 'pending':
              // 继续等待
              break;
            case 'error':
              cleanup();
              setStep('error');
              setMessage(result.message || '轮询失败');
              break;
            default:
              break;
          }
        }
      } catch (error) {
        console.error('轮询状态失败:', error);
        // 不立即停止，可能是临时网络问题
      }
    }, 2000); // 每2秒轮询一次
  };

  // 渲染当前账号信息
  const renderCurrentAccount = () => {
    if (!currentAccount || !currentAccount.has_cookie) return null;
    
    return (
      <div className="current-account">
        <div className="account-header">
          <span className="label">当前绑定账号</span>
        </div>
        <div className="account-info">
          {currentAccount.face && (
            <img 
              src={currentAccount.face} 
              alt="头像" 
              className="account-avatar"
              referrerPolicy="no-referrer"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          <div className="account-details">
            <span className="account-name">{currentAccount.uname || '未知用户'}</span>
            <span className="account-mid">UID: {currentAccount.mid}</span>
          </div>
          <span className="account-status active">已绑定</span>
        </div>
      </div>
    );
  };

  // 渲染二维码
  const renderQRCode = () => {
    if (!qrcodeUrl) return null;

    return (
      <div className="qrcode-container">
        <div className={`qrcode-wrapper ${step === 'scanned' ? 'scanned' : ''} ${step === 'expired' ? 'expired' : ''}`}>
          <QRCodeSVG 
            value={qrcodeUrl} 
            size={200}
            level="M"
            includeMargin={true}
          />
          {step === 'scanned' && (
            <div className="qrcode-overlay scanned">
              <span className="overlay-icon">✓</span>
              <span className="overlay-text">已扫码</span>
            </div>
          )}
          {step === 'expired' && (
            <div className="qrcode-overlay expired">
              <span className="overlay-icon">⟳</span>
              <span className="overlay-text">已过期</span>
            </div>
          )}
        </div>
        {step === 'qrcode' && (
          <div className="countdown">
            <span className="countdown-label">有效期:</span>
            <span className={`countdown-value ${countdown < 30 ? 'warning' : ''}`}>
              {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
            </span>
          </div>
        )}
      </div>
    );
  };

  // 渲染成功状态
  const renderSuccess = () => {
    if (!loginResult) return null;

    return (
      <div className="login-success">
        <div className="success-icon">✓</div>
        <div className="success-info">
          {loginResult.face && (
            <img 
              src={loginResult.face} 
              alt="头像" 
              className="success-avatar"
              referrerPolicy="no-referrer"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          <div className="success-details">
            <span className="success-name">{loginResult.uname}</span>
            <span className="success-mid">UID: {loginResult.mid}</span>
          </div>
        </div>
        <p className="success-message">登录成功！账号已绑定到此房间</p>
      </div>
    );
  };

  return (
    <div className="bilibili-qr-login">
      <div className="login-header">
        <h3>🔗 B站账号绑定</h3>
        <p className="login-desc">
          绑定后，该房间的直播互动、弹幕发送等将使用您的B站账号
        </p>
      </div>

      {renderCurrentAccount()}

      <div className="login-content">
        {step === 'idle' && (
          <div className="idle-state">
            <div className="bilibili-logo">📱</div>
            <p>扫码登录B站账号</p>
            <button className="btn btn-primary" onClick={generateQRCode}>
              生成登录二维码
            </button>
          </div>
        )}

        {step === 'loading' && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{message}</p>
          </div>
        )}

        {(step === 'qrcode' || step === 'scanned') && (
          <div className="qrcode-state">
            {renderQRCode()}
            <p className={`status-message ${step}`}>{message}</p>
            <p className="scan-tip">打开B站App → 扫一扫</p>
          </div>
        )}

        {step === 'expired' && (
          <div className="expired-state">
            {renderQRCode()}
            <p className="status-message expired">{message}</p>
            <button className="btn btn-primary" onClick={generateQRCode}>
              重新生成二维码
            </button>
          </div>
        )}

        {step === 'success' && renderSuccess()}

        {step === 'error' && (
          <div className="error-state">
            <div className="error-icon">✕</div>
            <p className="error-message">{message}</p>
            <button className="btn btn-primary" onClick={generateQRCode}>
              重试
            </button>
          </div>
        )}
      </div>

      <div className="login-footer">
        {step !== 'success' && onCancel && (
          <button className="btn btn-secondary" onClick={onCancel}>
            取消
          </button>
        )}
        {step === 'success' && onCancel && (
          <button className="btn btn-primary" onClick={onCancel}>
            完成
          </button>
        )}
      </div>
    </div>
  );
};

export default BilibiliQRLogin;
